import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import type { PedidoOperacionalInput } from "interfaces/loja/pedidoOperacional";
import {
  executarContinuarFluxoDestino,
  resolveContinuarFluxoPedido,
} from "utils/pedidoOperacional";
import { useNavigate } from "react-router-dom";

type PedidoContinuarFluxoProps = {
  input: PedidoOperacionalInput;
};

export default function PedidoContinuarFluxo({
  input,
}: PedidoContinuarFluxoProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark"
      ? alpha(theme.palette.primary.main, 0.35)
      : alpha("#1976d2", 0.2);

  const fluxo = resolveContinuarFluxoPedido(input);
  const statusNorm = String(input.status ?? "").toUpperCase();
  const encerradoSemSucesso =
    statusNorm === "CANCELADO" || statusNorm === "DEVOLVIDO";

  const onContinuar = () => {
    executarContinuarFluxoDestino(fluxo.destino, navigate);
  };

  if (fluxo.estadoFinal) {
    return null;
  }

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      }}
    >
      <MDBox p={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="button" fontWeight={700} color="primary">
              Continuar fluxo
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              title={fluxo.etapaTitulo}
            >
              {fluxo.etapaTitulo}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fluxo.descricao}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            flexShrink={0}
          >
            {fluxo.estadoFinal && (
              <Chip
                label={
                  encerradoSemSucesso
                    ? "Pedido encerrado (cancelado/estorno)"
                    : "Fluxo concluído"
                }
                color={encerradoSemSucesso ? "default" : "success"}
                size="small"
                variant="outlined"
                sx={{ padding: "16px" }}
              />
            )}
            {!fluxo.estadoFinal && !fluxo.podeNavegar && (
              <Chip
                label="Sem ação automática"
                size="small"
                variant="outlined"
              />
            )}
            {fluxo.podeNavegar ? (
              <MDButton
                variant="gradient"
                color="info"
                size="large"
                onClick={onContinuar}
                sx={{
                  position: "relative",
                  py: 1.35,
                  px: 3,
                  fontSize: "1rem",
                  fontWeight: 700,
                  minHeight: 52,
                  textTransform: "none",
                  animation: "continuarFluxoBtnPulse 2.4s ease-in-out infinite",
                  "@keyframes continuarFluxoBtnPulse": {
                    "0%, 100%": {
                      boxShadow: `0 2px 8px rgba(0,0,0,0.12), 0 0 0 0 ${alpha(theme.palette.info.main, 0.55)}`,
                    },
                    "50%": {
                      boxShadow: `0 2px 8px rgba(0,0,0,0.12), 0 0 0 14px ${alpha(theme.palette.info.main, 0)}, 0 0 28px 10px ${alpha(theme.palette.info.main, 0.4)}`,
                    },
                  },
                }}
              >
                {fluxo.etapaTitulo}
              </MDButton>
            ) : null}
          </Stack>
        </Stack>
      </MDBox>
    </Card>
  );
}
